import { useEffect, useState } from 'react';
import {
  BuildingStorefrontIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, storesApi, visitsApi, CompanyReport, Store, Visit } from '../services/api';

export default function Reports() {
  const [report, setReport] = useState<CompanyReport | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [reportData, storesData, visitsData] = await Promise.all([
        reportsApi.getCompanyReport(),
        storesApi.list(),
        visitsApi.list({ per_page: 1000 }).catch(() => ({ data: [], meta: { page: 1, per_page: 1000, total: 0 } })),
      ]);
      setReport(reportData);
      setStores(storesData);
      setVisits(visitsData.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Calculate store scores from visits
  const storeScores = stores.map(store => {
    const storeVisits = visits.filter(v => v.store_id === store.id);
    const avgScore = storeVisits.length > 0
      ? storeVisits.reduce((sum, v) => sum + (v.overall_score || 0), 0) / storeVisits.length
      : 0;
    return {
      ...store,
      avgScore,
      visitCount: storeVisits.length,
    };
  }).sort((a, b) => b.avgScore - a.avgScore);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-500">
          View KPIs and performance metrics
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        <div className="bg-white overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-lg p-3">
                <BuildingStorefrontIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Stores
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {report?.total_stores ?? 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-lg p-3">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Visits This Month
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {report?.total_visits_this_month ?? 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-orange-500 rounded-lg p-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Open Cases
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {report?.open_cases?.total ?? 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-teal-500 rounded-lg p-3">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Avg Score
                  </dt>
                  <dd className={`text-2xl font-semibold ${getScoreColor(report?.average_score ?? 0)}`}>
                    {Math.round(report?.average_score ?? 0)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Company Score */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white shadow-sm rounded-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Company Average Score</h3>
          <div className="flex items-center justify-center">
            <div className="relative">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle
                  className="text-gray-200"
                  strokeWidth="12"
                  stroke="currentColor"
                  fill="transparent"
                  r="58"
                  cx="80"
                  cy="80"
                />
                <circle
                  className={getScoreColor(report?.average_score ?? 0).replace('text-', 'text-')}
                  strokeWidth="12"
                  strokeDasharray={`${(report?.average_score ?? 0) * 3.64} 364`}
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="58"
                  cx="80"
                  cy="80"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-4xl font-bold ${getScoreColor(report?.average_score ?? 0)}`}>
                  {Math.round(report?.average_score ?? 0)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Score Distribution</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Excellent (90-100%)</span>
                <span className="text-green-600 font-medium">
                  {storeScores.filter(s => s.avgScore >= 90).length} stores
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${(storeScores.filter(s => s.avgScore >= 90).length / Math.max(storeScores.length, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Good (70-89%)</span>
                <span className="text-yellow-600 font-medium">
                  {storeScores.filter(s => s.avgScore >= 70 && s.avgScore < 90).length} stores
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full"
                  style={{ width: `${(storeScores.filter(s => s.avgScore >= 70 && s.avgScore < 90).length / Math.max(storeScores.length, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Needs Improvement (&lt;70%)</span>
                <span className="text-red-600 font-medium">
                  {storeScores.filter(s => s.avgScore < 70 && s.avgScore > 0).length} stores
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full"
                  style={{ width: `${(storeScores.filter(s => s.avgScore < 70 && s.avgScore > 0).length / Math.max(storeScores.length, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">No Visits Yet</span>
                <span className="text-gray-500 font-medium">
                  {storeScores.filter(s => s.avgScore === 0).length} stores
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-400 h-2 rounded-full"
                  style={{ width: `${(storeScores.filter(s => s.avgScore === 0).length / Math.max(storeScores.length, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Store Rankings */}
      <div className="bg-white shadow-sm rounded-xl p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Store Rankings</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visits
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Score
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score Bar
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {storeScores.map((store, index) => (
                <tr key={store.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-100 text-gray-800' :
                      index === 2 ? 'bg-orange-100 text-orange-800' : 'bg-white text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{store.name}</div>
                      <div className="text-xs text-gray-500">{store.city}, {store.state}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {store.visitCount}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-sm font-semibold ${getScoreColor(store.avgScore)}`}>
                      {store.avgScore > 0 ? `${Math.round(store.avgScore)}%` : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap w-40">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getScoreBgColor(store.avgScore)}`}
                        style={{ width: `${store.avgScore}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
